'use client'

import React, { useId, useState } from 'react'
import { GripVertical } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CompareSliderProps {
  before: React.ReactNode
  after: React.ReactNode
  className?: string
  initialPosition?: number
}

export function CompareSlider({
  before,
  after,
  className,
  initialPosition = 50,
}: CompareSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(initialPosition)
  const rangeId = useId()

  return (
    <div
      className={cn(
        'relative h-full w-full select-none overflow-hidden rounded-3xl border border-white/10 shadow-2xl focus-within:outline focus-within:outline-[3px] focus-within:outline-offset-2 focus-within:outline-white/40',
        className
      )}
    >
      <input
        id={rangeId}
        type="range"
        min={0}
        max={100}
        value={sliderPosition}
        onChange={(event) => setSliderPosition(Number(event.target.value))}
        className="absolute inset-0 z-30 h-full w-full cursor-ew-resize opacity-0"
        aria-label="Comparar antes y después"
        aria-valuetext={`${Math.round(sliderPosition)}%`}
      />
      {/* AFTER Content (Full width, always visible base) */}
      <div className="absolute inset-0 h-full w-full">{after}</div>

      {/* BEFORE Content (Clipped by slider position) */}
      <div
        className="absolute inset-0 h-full w-full overflow-hidden border-r border-white/20 bg-white/5 backdrop-blur-sm"
        style={{ width: `${sliderPosition}%` }}
      >
        <div className="absolute inset-0 h-full w-full">
          {/* We need to render 'before' in a container that matches parent width to avoid squishing */}
          <div className="absolute inset-0 h-full w-[100vw] max-w-[inherit]">{before}</div>
        </div>

        {/* Gradient overlay for depth at the cut */}
        <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-r from-transparent to-black/20" />
      </div>

      {/* Slider Handle */}
      <div
        className="group absolute inset-y-0 z-20 flex w-1 cursor-ew-resize items-center justify-center"
        style={{ left: `${sliderPosition}%` }}
      >
        {/* Line */}
        <div className="h-full w-px bg-white/50 shadow-[0_0_30px_2px_rgba(255,255,255,0.3)]" />

        {/* Handle Button */}
        <div className="absolute transform rounded-full bg-white p-2 text-slate-900 shadow-xl transition-transform active:scale-95 group-hover:scale-110">
          <GripVertical className="h-4 w-4" aria-hidden="true" />
        </div>
      </div>

      {/* Labels */}
      <div className="pointer-events-none absolute bottom-6 left-6 z-10 rounded-full border border-white/10 bg-black/50 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white/80 backdrop-blur-md">
        Antes: Caos Manual
      </div>
      <div className="pointer-events-none absolute bottom-6 right-6 z-10 rounded-full border border-blue-400/30 bg-blue-600/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-white backdrop-blur-md">
        Después: LuckAgents
      </div>
    </div>
  )
}
